import { useState, useMemo } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useApi } from '@/hooks/useApi';

interface SalesByPlatform {
  platform: string;
  revenue: number;
  units: number;
}

interface TopTitle {
  isbn: string;
  title: string;
  units: number;
  revenue: number;
}

interface SalesData {
  total_revenue: number;
  total_units: number;
  active_titles: number;
  sales_by_platform: SalesByPlatform[];
  top_titles: TopTitle[];
}

export default function DistSalesPage() {
  const [period, setPeriod] = useState('month');

  // Build API endpoint with period
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    return `distribution/sales?${params.toString()}`;
  }, [period]);

  // Fetch sales data from API
  const { data, isLoading, error, refetch } = useApi<SalesData>(apiEndpoint);

  const salesByPlatform = data?.sales_by_platform || [];
  const topTitles = data?.top_titles || [];
  const hasData = salesByPlatform.length > 0 || topTitles.length > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sales Reports</h1>
          <p className="text-slate-500 mt-1">Track revenue and sales performance</p>
        </div>
        <div className="flex gap-3">
          <Select
            options={[
              { value: 'day', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'year', label: 'This Year' },
            ]}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40"
            disabled
          />
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled
          >
            Refresh
          </Button>
          <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />} disabled>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
            <WrenchScrewdriverIcon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Coming Soon</h3>
            <p className="text-sm text-white/80">
              Sales reporting and analytics is currently under development. Stay tuned for updates!
            </p>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to load sales data</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />} disabled>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-500 mt-4">Loading sales data...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && !hasData && (
        <EmptyState
          icon={<ChartBarIcon className="w-12 h-12" />}
          title="No sales data found"
          description="Sales data will appear here once you have books distributed and sold on platforms"
        />
      )}

      {/* Sales Data - only show when there is data */}
      {!error && hasData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardBody className="text-center">
                <p className="text-4xl font-bold text-slate-800">
                  ${(data?.total_revenue || 0).toLocaleString()}
                </p>
                <p className="text-slate-500 mt-1">Total Revenue</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="text-center">
                <p className="text-4xl font-bold text-slate-800">
                  {(data?.total_units || 0).toLocaleString()}
                </p>
                <p className="text-slate-500 mt-1">Units Sold</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="text-center">
                <p className="text-4xl font-bold text-slate-800">
                  {data?.active_titles || 0}
                </p>
                <p className="text-slate-500 mt-1">Active Titles</p>
              </CardBody>
            </Card>
          </div>

          {/* Revenue by Platform Chart */}
          {salesByPlatform.length > 0 && (
            <Card>
              <CardHeader title="Revenue by Platform" />
              <CardBody>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesByPlatform}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="platform" stroke="#94A3B8" />
                      <YAxis stroke="#94A3B8" />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#4AC3C3" name="Revenue ($)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="units" fill="#F5C542" name="Units" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Top Titles */}
          {topTitles.length > 0 && (
            <Card>
              <CardHeader title="Top Selling Titles" />
              <CardBody className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">ISBN</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Units</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topTitles.map((title) => (
                      <tr key={title.isbn} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-sm">{title.isbn}</td>
                        <td className="px-4 py-3 font-medium">{title.title}</td>
                        <td className="px-4 py-3 text-right">{title.units}</td>
                        <td className="px-4 py-3 text-right font-medium">${title.revenue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

